// Org admin client — account bootstrap, members, invites, audit log,
// settings, and ownership/leave flows. Mirrors the backend's
// `src/railyard/routes/me.py` + `routes/org.py` cluster.

import type { components } from "@/lib/api-types";

import { seedDisabledFeatures } from "@/lib/feature-flags";

import {
  API,
  requestJson,
  requestOk,
} from "../api";

type Schemas = components["schemas"];


// ---------------------------------------------------------------------------
// Account bootstrap + me
//
// These three types are generated from the FastAPI response model on
// /api/me and /api/me/bootstrap (see routes/identity.py:MeResponse).
// `role` is a free-form string on the server today; the UI narrows it
// where it matters via OrgRole below.
// ---------------------------------------------------------------------------

export type MeUser = Schemas["MeUser"];
export type MeOrg = Schemas["MeOrg"];
export type MeResponse = Schemas["MeResponse"];

export type BootstrapResponse = Schemas["BootstrapResponse"];

export async function bootstrapMe(opts: {
  org_name?: string;
  display_name?: string;
  invite_token?: string;
  signup_attribution?: Record<string, string> | null;
} = {}): Promise<BootstrapResponse> {
  return requestJson(`${API}/api/me/bootstrap`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(opts),
  });
}

export async function fetchMe(
  opts?: { tenantRecoveryProbe?: boolean },
): Promise<MeResponse> {
  const res = await requestJson<MeResponse>(`${API}/api/me`, undefined, opts);
  // Seed the feature-flag cache here rather than at each of the three call
  // sites in auth-context, so a future fourth caller cannot silently reintroduce
  // the extra boot round trip (ENG-5390). The key must match the one AuthGuard
  // passes to useDisabledFeaturesState — `org?.id ?? me?.uid ?? null` — or the
  // seed is ignored and the settings fetch happens anyway.
  seedDisabledFeatures(res.disabled_features, res.org?.id ?? res.user?.uid ?? null);
  return res;
}

// Domain-match signup banner (ENG-2509, Phase 0b). `DomainMatch` is the
// minimal block bootstrap returns when the signer's verified email domain
// matches a workspace's claimed domain: display name + tenant id, nothing
// else about the workspace.
export type DomainMatch = Schemas["DomainMatch"];
export type RequestToJoinResponse = Schemas["RequestToJoinResponse"];

/** Ask to join the workspace that claimed the caller's verified email domain.
 * Records a pending request server-side (idempotent per user+workspace) and
 * notifies the workspace's admins + ops. It never grants membership — an
 * admin approves in Phase 1 (or sends a normal invite today). */
export async function requestToJoinWorkspace(
  tenantId: string,
): Promise<RequestToJoinResponse> {
  return requestJson(`${API}/api/me/request-to-join`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tenant_id: tenantId }),
  });
}

// Set or clear the caller's persisted default-workspace preference
// (ENG-3037). Purely advisory: the server never uses this to resolve the
// active workspace for a request — it's read back via GET /api/me to seed
// the workspace picker on a fresh device with no localStorage choice yet.
export async function updateDefaultWorkspace(
  tenantId: string | null,
): Promise<MeResponse> {
  return requestJson(`${API}/api/me`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ default_workspace_id: tenantId }),
  });
}

/** Set (or clear, passing `null`) the caller's IANA timezone (ENG-3796).
 *  Server-validated with `zoneinfo` — an unknown name 400s. Used by the
 *  explicit account settings field. Browser capture uses `captureTimezone`
 *  below so it cannot overwrite a saved choice. */
export async function updateTimezone(timezone: string | null): Promise<MeResponse> {
  return requestJson(`${API}/api/me`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ timezone }),
  });
}

/** Capture the browser's timezone only if the user has not chosen one yet. */
export async function captureTimezone(timezone: string): Promise<MeResponse> {
  return requestJson(`${API}/api/me/timezone/capture`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ timezone }),
  });
}

/** Set the caller's Morning Briefing recurring-email opt-in (ENG-3798).
 *  Defaults True server-side; also settable without login via the signed
 *  one-click unsubscribe link mailed on every briefing send. */
export async function updateBriefingEmails(briefingEmails: boolean): Promise<MeResponse> {
  return requestJson(`${API}/api/me`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ briefing_emails: briefingEmails }),
  });
}

/** Send a branded email-verification email via the backend (SendGrid + Firebase
 *  Admin SDK). The backend generates the Firebase oobCode, rebuilds the link to
 *  the branded /auth/action handler on the app host, and dispatches via SendGrid
 *  so the verification email carries our branding instead of Firebase's default.
 *
 *  `continuePath` is the same-origin path to return the user to after they click
 *  "Continue" in the handler (defaults to /new server-side). The caller passes
 *  the invite path when signup happens on an invite page so the invite is
 *  redeemed after verifying. */
export async function sendVerificationEmail(continuePath?: string): Promise<void> {
  await requestOk(`${API}/api/auth/send-verification`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ continue_path: continuePath ?? null }),
  });
}


// ---------------------------------------------------------------------------
// Org members (admin)
// ---------------------------------------------------------------------------

export type OrgRole = Schemas["MeUser"]["role"];

export type OrgMember = Schemas["OrgMember"];

export async function listOrgMembers(): Promise<OrgMember[]> {
  return requestJson(`${API}/api/org/members`);
}

// Name-resolution directory for the active workspace. Unlike listOrgMembers
// (admin Members panel — home-org users only), this also includes members who
// joined via workspace membership, so display surfaces like the share dialog
// can resolve a uid to a name/email instead of showing a raw Firebase uid.
// The `role` here reflects workspace access, not home-org role.
export async function getOrgDirectory(): Promise<OrgMember[]> {
  return requestJson(`${API}/api/org/directory`);
}

export async function updateOrgMemberRole(uid: string, role: OrgRole): Promise<OrgMember> {
  return requestJson(`${API}/api/org/members/${encodeURIComponent(uid)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
}

export async function removeOrgMember(uid: string): Promise<void> {
  await requestOk(
    `${API}/api/org/members/${encodeURIComponent(uid)}`,
    { method: "DELETE" },
    { allowStatus: [204] },
  );
}


// Invites were consolidated onto the workspace-membership model (ENG-1874).
// The "Invite member" flow + the accept page now use the workspace-invite
// functions in ./api/workspaces (createWorkspaceInvite, previewWorkspaceInvite,
// acceptWorkspaceInvite, …). The old /api/org/invites* client functions were
// removed with their backend routes.


// ---------------------------------------------------------------------------
// Org audit log (admin)
// ---------------------------------------------------------------------------

// Generated from routes/identity.py:AuditEvent. Hand-rolled version
// was missing `on_behalf_of_staff_uid` and `on_behalf_of_staff_email`
// (always emitted; populated only on staff-impersonation events).
export type AuditEvent = Schemas["AuditEvent"];

export async function listAuditEvents(limit = 100): Promise<AuditEvent[]> {
  return requestJson(`${API}/api/org/audit?limit=${limit}`);
}


// ---------------------------------------------------------------------------
// Org settings (rename / transfer / leave / logo)
// ---------------------------------------------------------------------------

export async function updateOrg(patch: {
  name?: string;
  accent_color?: string | null;
  font_id?: string | null;
  project_label?: string | null;
}): Promise<MeOrg> {
  return requestJson(`${API}/api/org`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

// ---------------------------------------------------------------------------
// Workspace brand-kit (admin) — the structured house brand decks render from.
//
// `BrandKit` is the GET response (the *resolved* kit: the authored kit if one
// exists, else a fallback synthesized from the Org brand, distinguished by
// `source`). `BrandKitWrite` is a partial PUT — every field optional; the
// server merges onto the effective kit and deep-merges `chrome`, so a write
// only needs to carry what changed. See routes/org.py + brand_kit.py.
// ---------------------------------------------------------------------------

export type BrandKit = Schemas["BrandKitModel"];
export type BrandKitWrite = Schemas["BrandKitWrite"];

export async function getBrandKit(): Promise<BrandKit> {
  return requestJson(`${API}/api/admin/brand-kit`);
}

export async function updateBrandKit(patch: BrandKitWrite): Promise<BrandKit> {
  return requestJson(`${API}/api/admin/brand-kit`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

// ---------------------------------------------------------------------------
// Brand-kit draft staging (ENG-3640) — show → confirm → use.
// A domain lookup stages a draft that is NEVER applied to decks until
// confirm. Preview is a real rendered title-slide PNG.
// ---------------------------------------------------------------------------

export type BrandKitDraft = {
  tenant_id: string;
  domain: string;
  name: string | null;
  description: string | null;
  primary: string | null;
  accent: string | null;
  secondary: string | null;
  font_heading: string | null;
  font_body: string | null;
  logo_url: string | null;
  logo_url_dark: string | null;
  colors: { hex: string; type: string }[];
  fonts: { name: string; type: string }[];
  source: string;
  created_at: string;
  updated_at: string;
  confirmed: boolean;
};

export async function getBrandKitDraft(): Promise<BrandKitDraft | null> {
  return requestJson(`${API}/api/admin/brand-kit/draft`);
}

export async function stageBrandKitDraft(domain: string): Promise<BrandKitDraft> {
  return requestJson(`${API}/api/admin/brand-kit/draft`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ domain }),
  });
}

export async function confirmBrandKitDraft(): Promise<BrandKit> {
  return requestJson(`${API}/api/admin/brand-kit/draft/confirm`, {
    method: "POST",
  });
}

export async function discardBrandKitDraft(): Promise<void> {
  await requestOk(`${API}/api/admin/brand-kit/draft`, { method: "DELETE" });
}

/** Server-relative path for the staged draft's rendered title-slide PNG. */
export function brandKitDraftPreviewPath(): string {
  return `/api/admin/brand-kit/draft/preview`;
}

// Firm deck-theme image assets (admin) — the per-tenant brand-kit asset store
// (role ∈ logo | logo_white | logo_mark[_white] | cover_texture | divider_watermark | divider_texture[_2|_3] | cover_motif). Multipart upload replaces the
// role's asset; DELETE clears it. Both return the updated `theme_assets` index
// + `urls` (role → cache-busted GET path). The GET serve path is admin-authed,
// so previews fetch it as a blob via `useAuthedBlobUrl` rather than a raw
// `<img src>` (which can't carry the Firebase bearer). See routes/org.py.
export type BrandAssets = Schemas["BrandAssetsResponse"];

/** Server-relative serve path for a role's brand asset (admin-authed). */
export function brandAssetPath(role: string): string {
  return `/api/admin/brand-kit/assets/${encodeURIComponent(role)}`;
}

export async function uploadBrandAsset(role: string, file: File): Promise<BrandAssets> {
  const form = new FormData();
  form.append("file", file);
  return requestJson(`${API}${brandAssetPath(role)}`, {
    method: "POST",
    body: form,
  });
}

export async function deleteBrandAsset(role: string): Promise<BrandAssets> {
  return requestJson(`${API}${brandAssetPath(role)}`, { method: "DELETE" });
}

export async function uploadOrgLogo(file: File): Promise<MeOrg> {
  const form = new FormData();
  form.append("file", file);
  return requestJson(`${API}/api/org/logo`, {
    method: "POST",
    body: form,
  });
}

export async function deleteOrgLogo(): Promise<MeOrg> {
  return requestJson(`${API}/api/org/logo`, { method: "DELETE" });
}

export async function transferOwnership(toUid: string): Promise<void> {
  await requestOk(`${API}/api/org/transfer-ownership`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ to_uid: toUid }),
  });
}

export async function leaveOrg(): Promise<void> {
  await requestOk(
    `${API}/api/org/leave`,
    { method: "POST" },
    { allowStatus: [204] },
  );
}
